import classes from './FormAlert.module.scss'
import {
  HiCheckCircle,
  HiXCircle,
  HiExclamationCircle,
  HiMinusCircle,
  HiExclamation
} from 'react-icons/hi'
import {
  useEffect,
  useId,
  useRef,
  useState
} from 'react'
import type { IconType } from 'react-icons'
import type { ReactNode } from 'react'

type AlertType = 'errorStatus' | 'noneStatus' | 'successStatus' | 'unchangedStatus' | 'warningStatus'

export interface ExpandableProps {
  isExpand: boolean
  expandButton: ReactNode
}

export interface NonExpandableProps {
  isExpand?: never
  expandButton?: never
}

export interface BaseProps {
  children: ReactNode
  title?: string
  type?: AlertType
  hasIcon?: boolean
  hasMaxHeight?: boolean
  hasBorder?: boolean
}

type Props = BaseProps & (ExpandableProps | NonExpandableProps)

const icons: Record<AlertType, IconType> = {
  errorStatus: HiExclamationCircle,
  noneStatus: HiXCircle,
  successStatus: HiCheckCircle,
  unchangedStatus: HiMinusCircle,
  warningStatus: HiExclamation
}

const StatusAlert = ({
  children,
  title = '',
  type = 'noneStatus',
  isExpand = false,
  expandButton,
  hasIcon = true,
  hasMaxHeight = true,
  hasBorder = true
// eslint-disable-next-line complexity
}: Props) => {
  const [isAtMaxHeight, setIsAtMaxHeight] = useState <boolean>(true)
  const contentContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const contentContainer = contentContainerRef.current

    if (contentContainer) {
      setIsAtMaxHeight(contentContainer.scrollHeight > contentContainer.clientHeight)
    }
  }, [contentContainerRef])

  useEffect(() => {
    const contentContainer = contentContainerRef.current

    if (!isExpand && contentContainer && expandButton) {
      contentContainer.scrollTo({
        top: 0,
        behavior: 'instant'
      })
    }
  }, [contentContainerRef, expandButton, isExpand])

  // There can be multiple errors on the page
  // So each needs a unique ID to prevent conflict
  const id = useId()

  const labelId = id + 'label'
  const descriptionId = id + 'description'

  const Icon = icons[type]

  const expandedClass = isExpand
    ? classes.isExpanded
    : ''

  const maxHeightClass = hasMaxHeight
    ? classes.hasMaxHeight
    : ''

  const borderClass = hasBorder
    ? ''
    : classes.hidesBorder

  const getClasses = (type: AlertType) => [
    classes.Root,
    expandedClass,
    borderClass,
    classes[type]
  ].filter(classStyle => classStyle).join(' ')

  const getContentClasses = () => [
    classes.content,
    expandedClass,
    maxHeightClass
  ].filter(classStyle => classStyle).join(' ')

  return (
    <div
      aria-describedby={descriptionId}
      aria-labelledby={labelId}
      className={getClasses(type)}
      role='alert'
    >
      {
        hasIcon &&
          <Icon
            aria-hidden
            className={classes.icon}
            size='16px'
          />
      }
      <div className={classes.contentWrapper}>
        <div
          className={getContentClasses()}
          ref={contentContainerRef}
        >
          {
            title &&
              <span
                className={classes.title}
                id={labelId}
              >
                {title}
              </span>
          }
          <div id={descriptionId}>
            {children}
          </div>
        </div>
        {
          isAtMaxHeight && expandButton &&
            <div className={classes.button}>
              {expandButton}
            </div>
        }
      </div>
    </div>
  )
}

export default StatusAlert
